import React, { useState, useEffect } from 'react';
import { X, UserPlus, Settings, Users, Trash2, Shield, AlertTriangle } from 'lucide-react';
import api from '../../services/api';

const ChannelSettingsModal = ({ channel, onClose, onUpdate }) => {
  const [channelDetails, setChannelDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (channel?.id) {
      loadChannelDetails();
    }
  }, [channel?.id]);

  const loadChannelDetails = async () => {
    setError(null);
    try {
      const response = await api.get(`/sigil/channels/${channel.id}`);
      setChannelDetails(response.data);
    } catch (error) {
      console.error('Error loading channel details:', error);
      setError(error.response?.data?.detail || 'Failed to load channel details');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm('Remove this member from the channel?')) return;

    try {
      await api.delete(`/sigil/channels/${channel.id}/members/${userId}`);
      loadChannelDetails();
      onUpdate();
    } catch (error) {
      console.error('Error removing member:', error);
      alert('Failed to remove member');
    }
  };

  const handleDeleteChannel = async () => {
    setDeleting(true);
    try {
      await api.delete(`/sigil/channels/${channel.id}`);
      onUpdate();
      onClose();
    } catch (error) {
      console.error('Error deleting channel:', error);
      alert(error.response?.data?.detail || 'Failed to delete channel. You may not have admin permissions.');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl p-6 border border-cyan-500/30">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white font-semibold">Channel Settings</span>
            <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg transition text-slate-400 hover:text-white">
              <X size={20} />
            </button>
          </div>
          <div className="flex items-center justify-center gap-3 text-cyan-300 py-12">
            <div className="w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            Loading...
          </div>
        </div>
      </div>
    );
  }

  // Error state or no data - show error with close button and delete option
  if (error || !channelDetails) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl p-6 border border-cyan-500/30">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white font-semibold">Channel Settings</span>
            <button onClick={onClose} className="p-2 hover:bg-slate-700 rounded-lg transition text-slate-400 hover:text-white">
              <X size={20} />
            </button>
          </div>
          <div className="text-center py-8">
            <AlertTriangle size={48} className="text-yellow-500 mx-auto mb-4" />
            <p className="text-slate-300 mb-2">{error || 'Unable to load channel details'}</p>
            <p className="text-sm text-slate-500 mb-6">You can still delete this conversation below.</p>

            {/* Allow delete even on error */}
            <div className="border-t border-red-500/30 pt-6">
              {!showDeleteConfirm ? (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 border border-red-500/30 transition mx-auto"
                >
                  <Trash2 size={16} />
                  Delete Conversation
                </button>
              ) : (
                <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30 max-w-md mx-auto">
                  <p className="text-red-300 text-sm mb-4">
                    Are you sure you want to delete this conversation?
                  </p>
                  <div className="flex gap-3 justify-center">
                    <button
                      onClick={handleDeleteChannel}
                      disabled={deleting}
                      className="flex items-center gap-2 px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition"
                    >
                      {deleting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 size={16} />
                          Yes, Delete
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={deleting}
                      className="px-4 py-2 text-sm bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl p-6 max-h-[80vh] overflow-y-auto border border-cyan-500/30">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-lg shadow-cyan-500/30">
              <Settings size={20} className="text-white" />
            </div>
            <h2 className="text-xl font-semibold text-white">
              Channel Settings
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-slate-700/50 hover:bg-red-500/20 rounded-lg transition text-slate-400 hover:text-red-400"
          >
            <X size={20} />
          </button>
        </div>

        {/* Channel Info */}
        <div className="bg-slate-700/30 rounded-xl p-5 border border-cyan-500/20 mb-6">
          <h3 className="text-lg font-semibold text-white mb-2">
            {channelDetails.name}
          </h3>
          {channelDetails.description && (
            <p className="text-slate-300 mb-3">{channelDetails.description}</p>
          )}
          <div className="flex items-center gap-4 text-sm">
            <span className="px-3 py-1 bg-cyan-500/20 text-cyan-300 rounded-full capitalize border border-cyan-500/30">
              {channelDetails.channel_type}
            </span>
            <span className="text-slate-400">
              {channelDetails.members?.length || 0} members
            </span>
          </div>
        </div>

        {/* Members List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users size={20} className="text-cyan-400" />
              Members ({channelDetails.members?.length || 0})
            </h3>
            <button
              className="flex items-center gap-2 px-4 py-2 text-sm bg-gradient-to-r from-cyan-500 to-blue-500 text-white rounded-lg hover:from-cyan-600 hover:to-blue-600 transition shadow-lg shadow-cyan-500/30"
            >
              <UserPlus size={16} />
              Add Members
            </button>
          </div>

          <div className="space-y-2">
            {channelDetails.members?.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl border border-cyan-500/10 hover:border-cyan-500/30 transition"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center text-white font-semibold shadow-lg shadow-cyan-500/30">
                    {(member.display_name || member.username || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="font-medium text-white flex items-center gap-2">
                      {member.display_name || member.username}
                      {member.is_admin && (
                        <span className="flex items-center gap-1 text-xs bg-gradient-to-r from-cyan-500/30 to-blue-500/30 text-cyan-300 px-2 py-0.5 rounded-full border border-cyan-500/30">
                          <Shield size={10} />
                          Admin
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-400">{member.email}</div>
                  </div>
                </div>
                {!member.is_admin && (
                  <button
                    onClick={() => handleRemoveMember(member.user_id)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-400 hover:text-white hover:bg-red-500/20 rounded-lg transition"
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Danger Zone - Delete Channel */}
        <div className="mt-8 pt-6 border-t border-red-500/30">
          <h3 className="text-lg font-semibold text-red-400 flex items-center gap-2 mb-4">
            <AlertTriangle size={20} />
            Danger Zone
          </h3>

          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 border border-red-500/30 transition"
            >
              <Trash2 size={16} />
              Delete Channel
            </button>
          ) : (
            <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
              <p className="text-red-300 text-sm mb-4">
                Are you sure you want to delete this channel? This action cannot be undone.
                All messages in this channel will be hidden.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleDeleteChannel}
                  disabled={deleting}
                  className="flex items-center gap-2 px-4 py-2 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50 transition"
                >
                  {deleting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Yes, Delete Channel
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="px-4 py-2 text-sm bg-slate-700/50 text-slate-300 rounded-lg hover:bg-slate-700 transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-slate-700/50 text-slate-300 rounded-xl hover:bg-slate-700 transition border border-cyan-500/20"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChannelSettingsModal;
